import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, LinearProgress, List, ListItem, ListItemIcon,
  ListItemText, Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ReplayIcon from '@mui/icons-material/Replay';
import { ApiRequest } from 'GlobalFunctions';

export interface BatchResult {
  order_id: number;
  company_order_id?: string;
  success: boolean;
  error?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  orderIds: number[];
  warehouseId: string;
  adjustedProducts: { product_id: number; quantity: number }[];
  excludeOrderIds?: number[];
  onDone: () => void;
}

export default function BatchInvoiceProgressDialog({ open, onClose, orderIds, warehouseId, adjustedProducts, excludeOrderIds, onDone }: Props) {
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState<BatchResult[]>([]);
  const [done, setDone] = useState(false);
  const [emptyOrderIds, setEmptyOrderIds] = useState<number[]>([]);
  const [emptyOrders, setEmptyOrders] = useState<{order_id: number; company_order_id: string}[]>([]);
  const [started, setStarted] = useState(false);
  const [batchId, setBatchId] = useState<number | null>(null);
  const [retrying, setRetrying] = useState(false);

  const doInvoice = (excludeIds: number[] = []) => {
    setProcessing(true);
    setResults([]);
    setDone(false);
    setEmptyOrderIds([]);
    setBatchId(null);
    ApiRequest({
      url: '/orders/batch-invoice',
      method: 'post',
      headers: { ltkn: localStorage.getItem('ltkn') },
      body: {
        order_ids: orderIds,
        warehouse_id: warehouseId,
        adjusted_products: adjustedProducts,
        exclude_order_ids: excludeIds,
      },
      setResponse: (r: any) => {
        setProcessing(false);
        if (r.status === 'empty_orders') {
          setEmptyOrderIds(r.emptyOrderIds || []);
          setEmptyOrders(r.emptyOrders || []);
        } else if (r.status === 'success') {
          setDone(true);
          setResults(r.results || []);
          if (r.batch_id) setBatchId(r.batch_id);
        } else {
          setDone(true);
          setResults([{ order_id: 0, success: false, error: r.message }]);
        }
      },
    });
  };

  const retryFailed = () => {
    if (!batchId) return;
    setRetrying(true);
    ApiRequest({
      url: '/orders/batch-retry',
      method: 'post',
      headers: { ltkn: localStorage.getItem('ltkn') },
      body: { batch_id: batchId },
      setResponse: (r: any) => {
        setRetrying(false);
        if (r.status === 'success') {
          // Merge results: keep previous successes, update retried ones
          const previousSuccesses = results.filter(res => res.success);
          const newResults = r.results || [];
          setResults([...previousSuccesses, ...newResults]);
        }
      },
    });
  };

  const handleClose = () => {
    if (done) onDone();
    onClose();
    setResults([]);
    setDone(false);
    setEmptyOrderIds([]);
    setEmptyOrders([]);
    setStarted(false);
    setBatchId(null);
    setRetrying(false);
  };

  // Auto-start on open
  if (open && !started) {
    setStarted(true);
    setTimeout(() => doInvoice(excludeOrderIds || []), 100);
  }

  const successCount = results.filter(r => r.success).length;
  const failCount = results.filter(r => !r.success).length;

  return (
    <Dialog open={open} onClose={done || emptyOrderIds.length ? handleClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle>Facturación en bloque</DialogTitle>
      <DialogContent>
        {(processing || retrying) && (
          <>
            <LinearProgress sx={{ mb: 2 }} />
            <p style={{ textAlign: 'center', color: '#555' }}>
              {retrying ? 'Reintentando fallidos...' : `Procesando ${orderIds.length} pedidos...`}
            </p>
          </>
        )}
        {emptyOrderIds.length > 0 && !processing && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Los siguientes pedidos quedarían sin productos al distribuir el stock: <strong>{emptyOrders.map(o => o.company_order_id).join(', ')}</strong>.
            ¿Deseas excluirlos y continuar con el resto?
          </Alert>
        )}
        {done && !retrying && (
          <>
            <Alert severity={failCount === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {failCount === 0
                ? `${successCount} factura${successCount > 1 ? 's' : ''} generada${successCount > 1 ? 's' : ''} exitosamente`
                : `${successCount} exitosa${successCount > 1 ? 's' : ''}, ${failCount} con error`}
            </Alert>
            <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
              {results.map((r, i) => (
                <ListItem key={i}>
                  <ListItemIcon>
                    {r.success ? <CheckCircleIcon color="success" /> : <ErrorIcon color="error" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={`Pedido #${r.company_order_id || r.order_id}`}
                    secondary={r.success ? 'Facturado' : r.error}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
        {!processing && !done && !emptyOrderIds.length && !retrying && (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <HourglassEmptyIcon sx={{ fontSize: 40, color: '#bbb' }} />
            <p>Preparando facturación...</p>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {emptyOrderIds.length > 0 && !processing && (
          <>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button variant="contained" color="warning" onClick={() => doInvoice(emptyOrderIds)}>
              Excluir y continuar
            </Button>
          </>
        )}
        {done && failCount > 0 && !retrying && (
          <Button onClick={retryFailed} color="warning" startIcon={<ReplayIcon />}>
            Reintentar fallidos ({failCount})
          </Button>
        )}
        {(done || (!emptyOrderIds.length && !processing)) && !retrying && (
          <Button onClick={handleClose} disabled={processing}>
            {done ? 'Cerrar' : 'Cancelar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
